import React, { useEffect, useMemo, useState } from 'react';
import CountDown from '@/src/components/ui/molecules/CountDown/CountDown';
import JamChatContainer from '@/src/components/ui/organisms/JamChatContainer/JamChatContainer';
import { AwsRegions } from '@/src/constants/shared';
import { JAM_EVENT_DETAILS_ROUTES } from '@/src/routes';
import { useCampaigns } from '@/src/store/campaigns.context';
import { useJamChallengeDetails } from '@/src/store/jam-challenge-details.context';
import { useJamChallenge } from '@/src/store/jam-challenge.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { IJamChallenge } from '@/src/types/JamChallenges';
import { i18nKeys } from '@/src/utils/i18n.utils';
import {
  jamChallengeOutputPropertiesRegex,
  jamChallengeOverviewRegex,
  jamChallengesPageRegex,
  jamChallengeTaskDetailsRegex,
  jamChatRegex,
} from '@/src/utils/myjam.utils';
import { Box, ColumnLayout, Grid } from '@amzn/awsui-components-react';
import { sum } from 'lodash';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styles from './EventFooter.module.scss';

interface IColumnContentProps {
  name: string;
  value?: string;
  'data-testid'?: string;
}

const ColumnContent: React.FC<IColumnContentProps> = ({ name, value, ...rest }) => (
  <Box variant="p" className="column-content">
    <span className={styles.columnItem}>{name}:</span>
    <Box variant="span" className="light-violet" data-testid={rest['data-testid'] || null}>
      &nbsp;{value ? value : rest.children}
    </Box>
  </Box>
);

export const EventFooter: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = history.location;
  const [scheduledTerminationTime, setScheduledTerminationTime] = useState<number | null>();
  const { selectedJamChallenge, challengeProgress, awsAccount, teamPropertiesResponse } = useJamChallengeDetails();
  const { jamChallengeData } = useJamChallenge();
  const { eventSlug, event } = useJamEventDetails();

  const isTeamChatScreen = jamChatRegex.test(pathname);
  const isTeamInfo = pathname.includes(JAM_EVENT_DETAILS_ROUTES.TeamInfo.resolve([eventSlug, '']));
  const JamChallengesScreen =
    jamChallengeOverviewRegex.test(pathname) ||
    jamChallengeTaskDetailsRegex.test(pathname) ||
    jamChallengeOutputPropertiesRegex.test(pathname);
  const isJamChallenges = jamChallengesPageRegex.test(pathname);
  const [Score, setScore] = useState(
    `${selectedJamChallenge?.score ?? '0'} / ${selectedJamChallenge?.possiblePoints ?? '150'}`
  );
  const { showCampaignRules, campaignAttempt, campaignStarted } = useCampaigns();
  const isCampaign = event?.type === 'CAMPAIGN_GROUP';
  const totalPoints = sum(jamChallengeData?.challenges?.map((challenge: IJamChallenge) => challenge.earnedPoints));

  const AwsAccount = awsAccount
    ? `${awsAccount.accountNumber} ${
        AwsRegions.ALL_AWS_REGIONS_BY_ID[awsAccount.region]
          ? AwsRegions.ALL_AWS_REGIONS_BY_ID[awsAccount.region].name
          : ''
      }`
    : '';

  useEffect(() => {
    const challenge = jamChallengeData?.challenges?.find((chal) => chal.id === selectedJamChallenge?.id);
    if (challenge !== undefined) {
      setScore(`${challenge.earnedPoints ?? '0'} / ${challenge.maxPoints ?? '150'}`);
    }
  }, [jamChallengeData, selectedJamChallenge]);

  const challengeStatus = useMemo(() => {
    if (challengeProgress?.completed) {
      return t(i18nKeys.myJams.eventTabs.label.completed);
    }
    if (challengeProgress?.started) {
      return t(i18nKeys.myJams.eventTabs.label.incomplete);
    }
    return t(i18nKeys.myJams.eventTabs.label.readyToStart);
  }, [challengeProgress]);

  /**
   * Determine the status of the current campaign attempt
   */
  const campaignAttemptStatus = useMemo(() => {
    // In Progress
    if (
      campaignAttempt &&
      campaignAttempt?.started &&
      !campaignAttempt?.completed &&
      campaignAttempt.attemptNumber > 0
    ) {
      return t(i18nKeys.myJams.eventTabs.label.inProgress);
    }

    // Failed
    if (
      campaignAttempt &&
      campaignAttempt?.completed &&
      !campaignAttempt?.passed &&
      campaignAttempt.attemptNumber > 0
    ) {
      return t(i18nKeys.myJams.eventTabs.label.failed);
    }

    // Passed
    if (campaignAttempt && campaignAttempt?.passed && campaignAttempt.attemptNumber > 0) {
      return t(i18nKeys.myJams.eventTabs.label.passed);
    }

    // Incomplete
    if (campaignAttempt?.started && campaignAttempt.attemptNumber > 0) {
      return t(i18nKeys.myJams.eventTabs.label.incomplete);
    }

    // Completed
    if (campaignAttempt?.completed && campaignAttempt.attemptNumber > 0) {
      return t(i18nKeys.myJams.eventTabs.label.completed);
    }

    return t(i18nKeys.myJams.eventTabs.label.readyToStart);
  }, [campaignAttempt, t]);

  useEffect(() => {
    if (teamPropertiesResponse && teamPropertiesResponse.scheduledTerminationTime) {
      setScheduledTerminationTime(teamPropertiesResponse.scheduledTerminationTime);
    }
  }, [teamPropertiesResponse]);

  if (isTeamInfo) {
    return null;
  }

  return (
    <div className={styles.eventFooterContainer} id="event-footer" data-testid="event-footer">
      {JamChallengesScreen && (
        <Grid gridDefinition={[{ colspan: 10 }]}>
          <ColumnLayout columns={5} variant="text-grid">
            <ColumnContent name={t(i18nKeys.footer.status)} value={challengeStatus} data-testid="challenge-status" />
            <ColumnContent name={t(i18nKeys.footer.score)} value={`${Score} pts`} data-testid="challenge-score" />
            {awsAccount && (
              <ColumnContent
                name={t(i18nKeys.footer.awsAccount)}
                value={AwsAccount}
                data-testid="challenge-aws-account"
              />
            )}
            {scheduledTerminationTime && (
              <ColumnContent name={t(i18nKeys.footer.labExpiration)} data-testid="challenge-lab-expiration">
                <CountDown format={'D_hh:mm:ss'} targetDate={scheduledTerminationTime} />
              </ColumnContent>
            )}
            {event?.eventEndDate && (
              <ColumnContent name={t(i18nKeys.footer.timeLeft)} data-testid="event-remaining-duration">
                <CountDown format={'D_hh:mm:ss'} targetDate={moment(event.eventEndDate).valueOf()} />
              </ColumnContent>
            )}
          </ColumnLayout>
        </Grid>
      )}
      {!isTeamChatScreen && <JamChatContainer />}
      {isCampaign && campaignAttempt && !event.ended && (showCampaignRules || !campaignStarted || isJamChallenges) && (
        <Grid gridDefinition={[{ colspan: 10 }]}>
          <ColumnLayout columns={4} variant="text-grid">
            <ColumnContent
              name={t(i18nKeys.footer.status)}
              value={campaignAttemptStatus}
              data-testid="campaign-attempt-status"
            />
            <ColumnContent name={t(i18nKeys.footer.points)} value={`${totalPoints}`} data-testid="campaign-score" />
          </ColumnLayout>
        </Grid>
      )}
    </div>
  );
};
